import React, { memo, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import { Flex, ListItem } from "@tremor/react";
import { updateEmployee } from "_redux/slices/timeline/employees";
import { CustomSpinner } from "_atoms";
import EmployeeInformation from "./employee-information";
import { EmployeeInformationColumnWidth } from '../timeline-constants';
import { selectSelectedTimeline } from "_redux/slices/timeline/timeline-areas";

const EmployeeRow = ({
  employee,
  zoneOptions,
  selectedZones = [],
}) => {

  const normalizeOptions = (zones) => Array.isArray(zones)
    ? zones
      .map((zone) => ({
        value: zone?.id ?? zone?._id ?? zone?.value ?? "",
        label: zone?.label ?? zone?.title ?? "",
      }))
      .filter((zone) => zone.value)
    : [];

  const areOptionsEqual = (a, b) => {
    if (a.length !== b.length) return false;
    const as = [...a].map((z) => `${z.value}::${z.label ?? ""}`).sort();
    const bs = [...b].map((z) => `${z.value}::${z.label ?? ""}`).sort();
    for (let i = 0; i < as.length; i++) {
      if (as[i] !== bs[i]) return false;
    }
    return true;
  };

  const dispatch = useDispatch();
  const selectedTimeline = useSelector(selectSelectedTimeline);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [initialOptions, setInitialOptions] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isError, setIsError] = useState(false);
  const zoneExists = useMemo(
    () => !!selectedTimeline?._id && selectedOptions.some((opt) => opt.value === selectedTimeline._id),
    [selectedOptions, selectedTimeline]
  );

  // sync local select value with incoming selected zones
  useEffect(() => {
    const normalized = normalizeOptions(selectedZones);
    // avoid overwriting in-flight edits
    if (isSaving) return;
    // if local changes differ from props, keep local until saved
    if (!areOptionsEqual(selectedOptions, initialOptions) && !areOptionsEqual(selectedOptions, normalized)) return;
    if (areOptionsEqual(selectedOptions, normalized) && areOptionsEqual(initialOptions, normalized)) return;
    setSelectedOptions(normalized);
    setInitialOptions(normalized);
    setIsError(false);
  }, [selectedZones]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasChanges = useMemo(() => {
    if (selectedOptions.length !== initialOptions.length) return true;
    const byKey = (arr) => arr.map((z) => `${z.value}::${z.label ?? ""}`).sort();
    const a = byKey(selectedOptions);
    const b = byKey(initialOptions);
    for (let i = 0; i < a.length; i++) {
      if (a[i] !== b[i]) return true;
    }
    return false;
  }, [selectedOptions, initialOptions]);

  const handleChange = (selection) => {
    const zones = Array.isArray(selection) ? selection.map((opt) => ({ id: opt.value, label: opt.label })) : [];
    setSelectedOptions(selection || []);
    setIsError(false);
  };

  const handleRemoveZone = () => {
    if (isSaving || !selectedTimeline?._id) return;
    const nextOptions = selectedOptions.filter((opt) => opt.value !== selectedTimeline._id);
    setSelectedOptions(nextOptions);
    setIsError(false);
  };

  const handleUpdate = () => {
    if (!hasChanges || isSaving) return;
    setIsSaving(true);
    const snapshotOptions = [...selectedOptions];
    const zones = Array.isArray(selectedOptions) ? selectedOptions.map((opt) => ({ id: opt.value, label: opt.label })) : [];
    dispatch(updateEmployee({ userId: employee?.userId, body: { workZone: zones } }))
      .then(() => {
        setInitialOptions(snapshotOptions);
        setIsError(false);
      })
      .catch(() => setIsError(true))
      .finally(() => setIsSaving(false));
  };

  const handleAddZone = () => {
    if (isSaving || !selectedTimeline?._id) return;
    const exists = selectedOptions.some((opt) => opt.value === selectedTimeline._id);
    if (exists) return;
    const nextOptions = [...selectedOptions, { value: selectedTimeline._id, label: selectedTimeline?.title ?? "" }];
    setSelectedOptions(nextOptions);
    setIsError(false);
  };

  // render menu in portal to avoid being clipped by list overflow
  const menuPortalTarget =
    typeof window !== "undefined" && window.document ? window.document.body : null;

  return (
    <ListItem>
      <div className="border-bottom pb-3" style={{ display: 'flex', justifyContent: 'space-between', flex: 1, alignItems: 'center', width: '100%' }}>

        {/* employee info */}
        <div style={{
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "flex-start",
          height: "100%",
          width: EmployeeInformationColumnWidth,
          flex: 2
        }}>
          <EmployeeInformation employee={employee} hideBorder />
        </div>

        {/* zone selector */}
        <div style={{ display: "flex", flex: 4 }}>
          <Select
            isMulti
            options={zoneOptions}
            value={selectedOptions}
            onChange={handleChange}
            closeMenuOnSelect={false}
            placeholder="Select zones"
            className="basic-multi-select"
            classNamePrefix="select"
            menuPortalTarget={menuPortalTarget}
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            }}
            width="100%"
          />
        </div>

        {/* update button */}
        <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8 }}>
            <button
              type="button"
              className={`btn btn-${isError ? "danger" : "primary"} btn-sm`}
              onClick={handleUpdate}
              disabled={!hasChanges || isSaving}
              style={{ minWidth: 80, maxWidth: 80 }}
            >
              {isSaving ? <CustomSpinner size={'small'} /> : "Update"}
            </button>
            {zoneExists ? (
              <button
                type="button"
                className="btn btn-danger btn-sm"
                onClick={handleRemoveZone}
                disabled={isSaving || !selectedTimeline?._id}
                style={{ minWidth: 80, maxWidth: 80 }}
              >
                Remove
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-success btn-sm"
                onClick={handleAddZone}
                disabled={isSaving || !selectedTimeline?._id}
                style={{ minWidth: 80, maxWidth: 80 }}
              >
                Add Zone
              </button>
            )}
          </div>
        </div>

      </div>
    </ListItem>
  );
};

export default memo(EmployeeRow);
